import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import showToast from "../components/Toast";
import { authHeader } from "../session";

const errorMessage = (error, fallback) =>
  error.response?.data?.error || fallback;

// ---------------------------------- Clubs -------------------------------------

export const getClubs = async () => {
  // Admins manage retired clubs too, so ask for the unfiltered list.
  const response = await axios.get("/api/v1/admin/clubs", {
    params: { includeInactive: "true" },
    headers: authHeader(),
  });
  return response.data.data;
};

export const useClubs = () =>
  useQuery("clubs", getClubs, { refetchOnWindowFocus: false });

export const createClub = async (club) => {
  const response = await axios.post("/api/v1/admin/clubs", club, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useCreateClub = () => {
  const queryClient = useQueryClient();

  return useMutation(createClub, {
    onSuccess: (club) => {
      queryClient.invalidateQueries("clubs");
      showToast(`Added ${club.name}.`);
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't add that club."), "error"),
  });
};

export const updateClub = async ({ id, ...club }) => {
  const response = await axios.put(`/api/v1/admin/clubs/${id}`, club, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useUpdateClub = () => {
  const queryClient = useQueryClient();

  return useMutation(updateClub, {
    onSuccess: (club) => {
      queryClient.invalidateQueries("clubs");
      showToast(`Saved changes to ${club.name}.`);
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't save those changes."), "error"),
  });
};

export const deleteClub = async (id) => {
  const response = await axios.delete(`/api/v1/admin/clubs/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

export const useDeleteClub = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteClub, {
    onSuccess: () => {
      queryClient.invalidateQueries("clubs");
      queryClient.invalidateQueries("courts");
      showToast("Club and its courts deleted.");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't delete that club."), "error"),
  });
};

// ---------------------------------- Courts ------------------------------------

export const getCourts = async (clubId) => {
  const response = await axios.get("/api/v1/admin/courts", {
    params: clubId ? { club: clubId } : {},
    headers: authHeader(),
  });
  return response.data.data;
};

export const useCourts = (clubId) =>
  useQuery(["courts", clubId ?? "all"], () => getCourts(clubId), {
    refetchOnWindowFocus: false,
  });

export const createCourt = async (court) => {
  const response = await axios.post("/api/v1/admin/courts", court, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useCreateCourt = () => {
  const queryClient = useQueryClient();

  return useMutation(createCourt, {
    onSuccess: (court) => {
      queryClient.invalidateQueries("courts");
      showToast(`Added ${court.name}.`);
    },
    onError: (error) =>
      // The API rejects two courts with the same name at one club; say so
      // rather than showing a raw duplicate-key error.
      showToast(
        errorMessage(error, "Couldn't add that court. Is the name already used?"),
        "error"
      ),
  });
};

export const updateCourt = async ({ id, ...court }) => {
  const response = await axios.put(`/api/v1/admin/courts/${id}`, court, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useUpdateCourt = () => {
  const queryClient = useQueryClient();

  return useMutation(updateCourt, {
    onSuccess: () => {
      queryClient.invalidateQueries("courts");
      showToast("Court updated.");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't update that court."), "error"),
  });
};

export const deleteCourt = async (id) => {
  const response = await axios.delete(`/api/v1/admin/courts/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

export const useDeleteCourt = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteCourt, {
    onSuccess: () => {
      queryClient.invalidateQueries("courts");
      showToast("Court deleted.");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't delete that court."), "error"),
  });
};
